import { forwardRef, useState } from "react";
import { cn } from "src/shadcn/lib/utils";
import {
  Combobox,
  ComboboxContent,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
} from "src/primitives/Combobox";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { SelectAnchor } from "./SelectAnchor";
import type { MultiSelectComboboxProps } from "./MultiSelectCombobox.types";

export const MultiSelectCombobox = forwardRef<
  HTMLInputElement,
  MultiSelectComboboxProps
>(
  (
    {
      name,
      id,
      effectiveItems,
      labelForValue,
      renderOption,
      isSearchable,
      isAsync,
      isCreatable,
      creatableFilter,
      isControlled,
      valueProp,
      defaultValue,
      selectedValues,
      handleValueChange,
      handleInputValueChange,
      onOpenChange,
      isDisabled,
      autoHighlight,
      required,
      sizeConfig,
      error,
      chipsClassName,
      inputClassName,
      placeholder,
      hideChipRemove,
      ariaDescribedBy,
      testIdPrefix,
      contentProps,
      dropdownProps,
      children,
    },
    ref
  ) => {
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    return (
      // @ts-expect-error — conditional spread creates a union that TS cannot narrow to the Combobox discriminated-union props
      <Combobox
        multiple
        name={name}
        {...(effectiveItems && !children
          ? {
              items: effectiveItems,
              itemToStringLabel: labelForValue as (val: unknown) => string,
              ...(!isSearchable || isAsync
                ? { filter: null }
                : isCreatable
                  ? { filter: creatableFilter }
                  : {}),
            }
          : {})}
        {...(isControlled ? { value: valueProp } : { defaultValue })}
        onValueChange={handleValueChange as (...args: unknown[]) => void}
        {...(isCreatable || isAsync
          ? { onInputValueChange: handleInputValueChange }
          : {})}
        onOpenChange={onOpenChange}
        disabled={isDisabled}
        autoHighlight={autoHighlight}
      >
        <SelectAnchor
          ref={setAnchorEl}
          isSearchable={isSearchable}
          className={!isSearchable ? "cursor-pointer" : undefined}
        >
          <ComboboxChips
            className={cn(
              sizeConfig.chips,
              !!error &&
                "border-destructive ring-3 ring-destructive/20 dark:border-destructive/50 dark:ring-destructive/40",
              chipsClassName,
              inputClassName
            )}
            {...(testIdPrefix
              ? { "data-testid": `${testIdPrefix}-select-container` }
              : {})}
          >
            {selectedValues.map(val => (
              <ComboboxChip
                key={val}
                className={sizeConfig.chip}
                showRemove={!hideChipRemove}
                {...(testIdPrefix
                  ? { "data-testid": `${testIdPrefix}-chip` }
                  : {})}
              >
                {renderOption(val)}
              </ComboboxChip>
            ))}
            <ComboboxChipsInput
              ref={ref}
              id={id}
              placeholder={
                isSearchable && selectedValues.length === 0
                  ? placeholder
                  : undefined
              }
              aria-invalid={!!error || undefined}
              aria-describedby={ariaDescribedBy}
              aria-required={required || undefined}
              className={cn(
                sizeConfig.input,
                !isSearchable && "w-0 min-w-0 overflow-hidden p-0 opacity-0"
              )}
              disabled={isDisabled}
              {...(testIdPrefix
                ? { "data-testid": `${testIdPrefix}-select-input` }
                : {})}
            />
          </ComboboxChips>
        </SelectAnchor>
        <ComboboxContent anchor={anchorEl} {...contentProps}>
          <MultiSelectDropdown
            {...dropdownProps}
            renderOption={renderOption}
            testIdPrefix={testIdPrefix}
          >
            {children}
          </MultiSelectDropdown>
        </ComboboxContent>
      </Combobox>
    );
  }
);
MultiSelectCombobox.displayName = "MultiSelectCombobox";
